import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Calendar, RefreshCw, X } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import {
  TechPastCallJobDetailMode,
  TechPastCallJobDetailResponse,
  TechPastCallsResponse,
  TechPastCallsRow,
  TechPastCallsSummary,
  getTechPastCallJobDetail,
  getTechPastCalls,
} from '@/lib/tech-api-client';

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  return `${Math.round(value * 100)}%`;
}

function SummaryCard({ title, value, sub }: { title: string; value: string; sub?: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4">
      <div className="text-sm font-medium text-gray-600">{title}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
}

function Badge({ label, tone }: { label: string; tone: 'green' | 'gray' | 'amber' }) {
  const cls =
    tone === 'green'
      ? 'bg-green-100 text-green-800'
      : tone === 'amber'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-gray-100 text-gray-700';

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function membershipBadge(row: TechPastCallsRow, which: 'atTime' | 'now') {
  const badge = which === 'atTime' ? row.membershipAtTime : row.membershipNow;
  if (badge.status === 'member') {
    const suffix = badge.source === 'internal' ? 'Internal' : badge.source === 'successware' ? 'SW' : '';
    const label = suffix ? `Member (${suffix})` : 'Member';
    return <Badge label={label} tone="green" />;
  }
  return <Badge label="Non-member" tone="gray" />;
}

function getDefaultDays(technicianEmployeeCode: string | null | undefined): number {
  if (!technicianEmployeeCode) return 90;
  return 90;
}

function safeJsonStringify(value: unknown, maxChars = 200_000): string {
  let raw = '';
  try {
    raw = JSON.stringify(value, null, 2);
  } catch {
    raw = String(value);
  }

  if (raw.length <= maxChars) return raw;
  return `${raw.slice(0, maxChars)}\n\n… (truncated to ${maxChars.toLocaleString()} chars)`;
}

export default function TechPastCallsPage() {
  const { technician, accessToken } = useTechAuth();

  const [days, setDays] = useState(() => getDefaultDays(technician?.successware_employee_code));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TechPastCallsResponse | null>(null);

  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [jobModalRow, setJobModalRow] = useState<TechPastCallsRow | null>(null);
  const [jobDetailMode, setJobDetailMode] = useState<TechPastCallJobDetailMode>('summary');
  const [jobDetailLoading, setJobDetailLoading] = useState(false);
  const [jobDetailError, setJobDetailError] = useState<string | null>(null);
  const [jobDetail, setJobDetail] = useState<TechPastCallJobDetailResponse | null>(null);
  const [jobDetailReloadKey, setJobDetailReloadKey] = useState(0);

  const summary: TechPastCallsSummary = useMemo(() => {
    return (
      data?.summary || {
        totalPastCalls: 0,
        eligibleOpportunities: 0,
        agreementsSold: 0,
        conversionPct: 0,
        lostOpportunities: 0,
        spiffEarnedUsd: 0,
        spiffLostUsd: 0,
      }
    );
  }, [data]);

  const warnings = data?.warnings || [];

  const load = async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);

    try {
      const res = await getTechPastCalls(accessToken, { days, size: 100, page: 0 });
      setData(res);
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : 'Failed to load past calls.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, days]);

  const rows = data?.rows || [];

  const openJobModal = (row: TechPastCallsRow) => {
    setJobModalRow(row);
    setJobDetailMode('summary');
    setJobDetail(null);
    setJobDetailError(null);
    setJobModalOpen(true);
  };

  const closeJobModal = () => {
    setJobModalOpen(false);
    setJobModalRow(null);
    setJobDetail(null);
    setJobDetailError(null);
    setJobDetailReloadKey(0);
  };

  useEffect(() => {
    const fetchDetail = async () => {
      if (!jobModalOpen) return;
      if (!accessToken) return;
      if (!jobModalRow?.jobId) {
        setJobDetailError('Missing jobId for this row.');
        return;
      }

      setJobDetailLoading(true);
      setJobDetailError(null);

      try {
        const res = await getTechPastCallJobDetail(accessToken, jobModalRow.jobId, jobDetailMode);
        setJobDetail(res);
      } catch (e) {
        setJobDetail(null);
        setJobDetailError(e instanceof Error ? e.message : 'Failed to load job detail.');
      } finally {
        setJobDetailLoading(false);
      }
    };

    void fetchDetail();
  }, [accessToken, jobDetailMode, jobDetailReloadKey, jobModalOpen, jobModalRow?.jobId]);

  const jobDetailJson = useMemo(() => {
    return jobDetail?.detail ? safeJsonStringify(jobDetail.detail) : '';
  }, [jobDetail?.detail]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-2xl font-semibold text-gray-900">Past Calls</div>
          <div className="mt-1 text-sm text-gray-600">
            Primary-tech jobs (no future calls) pulled from SuccessWare assignments.
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <Calendar className="w-4 h-4" />
            <span className="font-medium">Date range</span>
            <select
              className="ml-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              disabled={loading}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
              <option value={365}>Last 365 days</option>
            </select>
          </label>

          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 text-white px-4 py-2 text-sm font-medium hover:bg-gray-800 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {!technician?.successware_employee_code ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-amber-900">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div>
              <div className="font-semibold">Missing SuccessWare employee code</div>
              <div className="mt-1 text-sm">
                This dashboard requires your SuccessWare employee code to find your primary-tech jobs.
                Ask your manager to add it in the Admin portal.
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-amber-900">
          <div className="font-semibold">Notes</div>
          <ul className="mt-2 list-disc list-inside text-sm">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-900">
          <div className="font-semibold">Failed to load past calls</div>
          <div className="mt-1 text-sm">{error}</div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard title="Past calls" value={String(summary.totalPastCalls)} sub="Primary tech only" />
        <SummaryCard title="Eligible opportunities" value={String(summary.eligibleOpportunities)} sub="Non-member at job time" />
        <SummaryCard
          title="Agreements sold"
          value={String(summary.agreementsSold)}
          sub={`Conversion: ${formatPct(summary.conversionPct)}`}
        />
        <SummaryCard title="Spiff impact" value={`$${summary.spiffEarnedUsd}`} sub={`Lost: $${summary.spiffLostUsd}`} />
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-4">
          <div className="text-sm font-semibold text-gray-900">Jobs</div>
          <div className="text-xs text-gray-500">Tap a row to open Job Detail • Showing {rows.length}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Scheduled</th>
                <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Job #</th>
                <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Type</th>
                <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Customer</th>
                <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Member @ Job</th>
                <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Member Now</th>
                <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Sold</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row, idx) => {
                const soldCount = row.agreementsSoldOnJob.count;
                const soldLabel = soldCount > 0 ? `${soldCount}` : '—';
                const soldTone = soldCount > 0 ? 'green' : row.opportunity ? 'amber' : 'gray';
                const customerLabel = [row.customerName, row.address].filter(Boolean).join(' • ') || '—';

                return (
                  <tr
                    key={`${row.jobId || row.jobNumber || idx}`}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => openJobModal(row)}
                    role="button"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {row.scheduledForLocal || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {row.jobNumber || row.jobId || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {[row.jobType, row.jobClass].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="px-4 py-3 min-w-[320px] text-gray-700">{customerLabel}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{membershipBadge(row, 'atTime')}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{membershipBadge(row, 'now')}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge label={soldLabel} tone={soldTone} />
                      {soldCount > 0 && row.agreementsSoldOnJob.codes.length > 0 ? (
                        <div className="mt-1 text-xs text-gray-500">
                          {row.agreementsSoldOnJob.codes.slice(0, 3).join(', ')}
                          {row.agreementsSoldOnJob.codes.length > 3 ? '…' : ''}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}

              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    {loading ? 'Loading…' : 'No past calls found for this range.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {jobModalOpen && jobModalRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeJobModal} />

          <div className="relative w-full max-w-5xl max-h-[85vh] bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-semibold text-gray-900">Job Detail</div>
                <div className="mt-1 text-sm text-gray-600">
                  Job: {jobModalRow.jobNumber || jobModalRow.jobId || '—'}
                  {jobModalRow.scheduledForLocal ? ` • ${jobModalRow.scheduledForLocal}` : ''}
                </div>
              </div>

              <button
                type="button"
                onClick={closeJobModal}
                className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-gray-700">
                Customer: {[jobModalRow.customerName, jobModalRow.address].filter(Boolean).join(' • ') || '—'}
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">
                  Mode
                  <select
                    className="ml-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"
                    value={jobDetailMode}
                    onChange={(e) => setJobDetailMode(e.target.value as TechPastCallJobDetailMode)}
                    disabled={jobDetailLoading}
                  >
                    <option value="summary">Summary</option>
                    <option value="full">Full</option>
                  </select>
                </label>

                <button
                  type="button"
                  onClick={() => {
                    setJobDetail(null);
                    setJobDetailError(null);
                    setJobDetailReloadKey((value) => value + 1);
                  }}
                  disabled={jobDetailLoading}
                  className="inline-flex items-center gap-2 rounded-xl bg-gray-900 text-white px-4 py-2 text-sm font-medium hover:bg-gray-800 disabled:opacity-60"
                >
                  <RefreshCw className={`w-4 h-4 ${jobDetailLoading ? 'animate-spin' : ''}`} />
                  Reload
                </button>
              </div>
            </div>

            {jobDetailError ? (
              <div className="px-5 py-4 bg-red-50 border-b border-red-200 text-red-900">
                <div className="font-semibold">Failed to load job detail</div>
                <div className="mt-1 text-sm">{jobDetailError}</div>
              </div>
            ) : null}

            <div className="p-5 overflow-auto max-h-[calc(85vh-160px)]">
              {jobDetailLoading ? (
                <div className="text-sm text-gray-600">Loading…</div>
              ) : jobDetail?.detail ? (
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded-2xl p-4 overflow-auto">
                  {jobDetailJson}
                </pre>
              ) : (
                <div className="text-sm text-gray-600">No detail loaded.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
